/**
 * The set's own artwork: the thumbnails a shelf or a door shows.
 *
 * Until 2026-09-24 every one of these was a grey rectangle with an icon in it,
 * and that was honest — we take no media from vendy (`prd/DIVERGENCE.md`), and
 * there was nowhere else to get any. `pnpm imagen` changed the second half of
 * that sentence, so a placeholder became a debt rather than a decision (A13).
 *
 * Resolved by key, like the presenters and for the same reason: the catalog row
 * carries `annotated-product`, not a URL, and a URL in a row is a link that
 * breaks silently the day the file is renamed.
 *
 * **What is in them.** Mock layouts, generic unbranded objects, and no readable
 * text anywhere — where a layout implies words they are blurred grey bars.
 * Generated lettering is unreliable, and a broken word on a shop window is
 * worse than an empty frame. The prompts are `infra/specs/layouts.json` and
 * `infra/specs/doors.json`; `docs/ASSETS.md` is how they are made again.
 */
const LAYOUTS = import.meta.glob('./artwork/layouts/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const DOORS = import.meta.glob('./artwork/doors/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const byKey = (files: Record<string, string>) =>
  new Map(
    Object.entries(files).map(([path, url]) => [path.replace(/^.*\/(.+)\.webp$/, '$1'), url]),
  );

const LAYOUT_ART = byKey(LAYOUTS);
const DOOR_ART = byKey(DOORS);

/**
 * `undefined` when this build carries no artwork for that key — which is not an
 * error. A catalog gains rows by migration, so a key can legitimately arrive
 * before its picture does, and the caller falls back to the icon it used to
 * show rather than to a broken image.
 */
export function layoutArtwork(key: string): string | undefined {
  return LAYOUT_ART.get(key);
}

export function doorArtwork(key: string): string | undefined {
  return DOOR_ART.get(key);
}

const SHELF = import.meta.glob('./artwork/shelf/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const SHELF_ART = [...byKey(SHELF).entries()]
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, url]) => url);

/**
 * A backdrop for a ready-made advert, chosen by its key rather than stored
 * against it.
 *
 * Twenty photographs for thirty-six templates, so they share — and sharing is
 * the honest arrangement rather than a shortage. The original's poster is a
 * frame of the advert that template actually produces; ours cannot be, because
 * no advert exists yet. A backdrop is scenery, and scenery repeats.
 *
 * Picked by a hash of the key so that it is stable — the same template shows
 * the same photograph on every render and every machine — and so that
 * neighbours in the grid do not collide, which sorting by key would guarantee
 * they did.
 */
export function shelfArtwork(key: string): string | undefined {
  if (SHELF_ART.length === 0) return undefined;
  let hash = 0;
  for (const char of key) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return SHELF_ART[hash % SHELF_ART.length];
}

const OPTIONS = import.meta.glob('./artwork/options/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const OPTION_ART = byKey(OPTIONS);

/**
 * The picture beside an answer on the funnel's `image_choice` screens.
 *
 * The original has one per option — the measurement says so outright («вариант:
 * подпись и пояснение в столбик, картинка справа») — and ours was a grey square
 * with an icon in it, on a screen whose whole kind is named after the picture.
 *
 * One per key and shared across option sets where the key is shared: `quality`
 * is «people who pay for good» on both the service and the product branch, and
 * that is one answer asked twice rather than two answers.
 */
export function optionArtwork(key: string): string | undefined {
  return OPTION_ART.get(key);
}

const WALL = import.meta.glob('./artwork/wall/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const WALL_ART = [...byKey(WALL).entries()]
  .filter(([key]) => key.startsWith('wall-'))
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([, url]) => url);

const FUNNEL_ART = byKey(WALL);

/**
 * The wall on the funnel's first screen — eight frames of finished adverts.
 *
 * Grey rectangles from April until 2026-09-25, under a caption that says «real
 * ads made here»: the one block on the entrance whose whole job is to show
 * work, showing none.
 *
 * By index rather than by key, because these tiles have no keys — they are a
 * wall, and which frame lands where is arbitrary so long as it is stable.
 */
export function wallArtwork(index: number): string | undefined {
  return WALL_ART.length === 0 ? undefined : WALL_ART[index % WALL_ART.length];
}

/** A named picture on a funnel screen: `photo-own`, `photo-stills`. */
export function funnelArtwork(key: string): string | undefined {
  return FUNNEL_ART.get(key);
}

const SAMPLES = import.meta.glob('./artwork/samples/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const SAMPLE_ART = byKey(SAMPLES);

/**
 * A sample advert's picture — the one the image hub hands to the composer as a
 * reference.
 *
 * One per sample and no sharing, unlike the shelf backdrops: a backdrop is
 * scenery and repeats, but this picture *is* the thing being cloned. Two
 * samples with the same reference are one sample listed twice.
 */
export function sampleArtwork(slug: unknown): string | undefined {
  return typeof slug === 'string' ? SAMPLE_ART.get(slug) : undefined;
}
